<script setup>
// Astra DS · HeroAgenda — la agenda de conciertos, en franja de lado a lado.
//
// Figma `hero · agenda en franja 2586:14920`, dentro de `Template/Home-Reset
// 659:2862`. Entrega `2026-10-21-portada-franja-y-player-en-cabecera.md`,
// aprobada por Pablo el 21.10.26. Sustituye al hero de dos mitades (emisión +
// agenda, `1380:181`): **lo que suena se va a la cabecera** y la portada abre con
// los conciertos.
//
//   foto 16:9 que cubre · velo al pie · regla arriba
//   nombre + ChipFecha
//   frase
//   filete · sala · ciudad ········· compra · 01 / 04 ‹ ›
//
// 🔴 **La imagen es una foto limpia, no un cartel.** Con la letra del cartel y el
// nombre nuestro encima no se lee ninguno; por eso los carteles de la redacción
// no llegan aquí (`getAgenda()`, Pablo, 21.10.26). La foto de Ticketmaster es
// 16:9 y la franja es más ancha (27 : 10): se reduce hasta el ancho y se ve desde
// su borde de arriba, salvo el fondo liso que el encuadre le quita (ver más
// abajo); lo que sobra se recorta por abajo, nunca a los lados.
//
// **Un artista con varias fechas** (Figma `Home · hero con varias fechas
// 2589:15203`, entrega `2026-10-21-portada-varias-fechas.md`): un solo paso,
// sin chip, y bajo el filete las fechas en columnas con su sala · ciudad y, a
// su derecha, **su compra** (`Columna de fecha · dos compras 2591:15160`): cada
// fecha se compra aparte, como pide el contrato (§ 2-bis), que no deja usar el
// enlace de una para otra. A la derecha de la franja solo quedan las flechas.
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import DuotoneArt from "@/components/ds/DuotoneArt.vue";
import ButtonDs from "@/components/ds/ButtonDs.vue";
import CtaButton from "@/components/ds/CtaButton.vue";
import BrandTicketmaster from "@/components/ds/BrandTicketmaster.vue";
import ChipFecha from "@/components/ds/ChipFecha.vue";
import AstraIcon from "@/components/icons/AstraIcon.vue";
import { medir } from "@/services/medicion";
import { medirMargenes, anclajeVertical } from "@/utils/encuadre";
import { parametroDev } from "@/utils/parametrosDev";

const props = defineProps({
  // Lo que devuelve `getAgenda()`: un paso por artista, ya ordenado.
  conciertos: { type: Array, default: () => [] },

  // Lo que dura cada paso. Ocho segundos es lo que tarda en leerse la franja.
  intervalo: { type: Number, default: 8000 },
});

const paso = ref(0);
// De 0 a 1. Se pinta como ancho de la regla, y se reinicia en cada salto.
const avance = ref(0);

let reloj = null;
let desde = 0;

const total = computed(() => props.conciertos.length);
// Con un solo concierto no hay regla, ni contador, ni flechas.
const gira = computed(() => total.value > 1);
const actual = computed(() => props.conciertos[paso.value] || null);

// ── La foto, y cómo se releva por la siguiente ──
//
// La que se ve **no se va hasta que la siguiente está**: dos capas, la que se
// muestra y la que entra, y la de debajo solo se cambia cuando la de encima ya ha
// cargado. Sin esto, cada paso empezaba en un hueco gris.
const arte = computed(() => actual.value?.cartel || "");
const mostrado = ref("");
const entrante = ref("");
const cargada = ref(false);
let relevo = null;

// La foto del paso siguiente, pedida por detrás: cada salto empezaba de cero.
function precargaElSiguiente() {
  if (typeof window === "undefined" || total.value < 2) return;
  if (navigator.connection?.saveData) return;
  const siguiente = props.conciertos[(paso.value + 1) % total.value]?.cartel;
  if (!siguiente) return;
  pedirMargenes(siguiente);
  const img = new Image();
  img.decoding = "async";
  img.src = siguiente;
}

function alCargar() {
  cargada.value = true;
  precargaElSiguiente();
  clearTimeout(relevo);
  relevo = setTimeout(() => {
    mostrado.value = entrante.value;
    entrante.value = "";
  }, 320);
}

watch(
  arte,
  (nuevo) => {
    if (nuevo === mostrado.value) {
      entrante.value = "";
      return;
    }
    clearTimeout(relevo);
    cargada.value = false;
    entrante.value = nuevo;
  },
  { immediate: true },
);

// ── El encuadre (Pablo, 21.10.26) ──
//
// La foto se ancla arriba, justo bajo la cabecera. Si trae fondo liso arriba
// (la pared del estudio, un negro), sube lo justo para que su contenido quede
// centrado en la franja, sin comerse nunca lo de arriba: ver
// `utils/encuadre.js`, y entero en
// `docs/ENCUADRE_DE_FOTOS.md`. Sin margen que aprovechar, o si no se pueden leer los
// píxeles, se queda anclada arriba. En local, `?encuadre=0` lo apaga para
// comparar.
const franja = ref(null);
const visor = ref({ ancho: 0, alto: 0 });
const margenes = ref({});
let observador = null;

const encuadreApagado = parametroDev("encuadre") === "0";

function pedirMargenes(url) {
  if (!url || encuadreApagado || url in margenes.value) return;
  medirMargenes(url).then((m) => {
    margenes.value = { ...margenes.value, [url]: m };
  });
}

function anclaje(url) {
  const y = anclajeVertical(margenes.value[url], visor.value.ancho, visor.value.alto);
  return y == null ? "top" : `center ${y.toFixed(1)}%`;
}

watch(arte, pedirMargenes, { immediate: true });

// ── Los datos ──

// «Razzmatazz 1 · Barcelona». Lo que falte no deja hueco.
const linea = computed(() => {
  const c = actual.value;
  if (!c) return [];
  return [c.sala, c.ciudad].filter(Boolean);
});

const fechas = computed(() => actual.value?.fechas || []);
const varias = computed(() => fechas.value.length > 1);

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
// «27 NOV», como en el chip.
function diaYMes(fecha) {
  const [, m, d] = String(fecha).slice(0, 10).split("-").map(Number);
  return d && m ? `${d} ${MESES[m - 1]}` : "";
}

const dos = (n) => String(n).padStart(2, "0");
const contador = computed(() => `${dos(paso.value + 1)} / ${dos(total.value)}`);

// La compra de la derecha, con una sola fecha. Con varias, cada una lleva la suya.
const destino = computed(() => {
  if (varias.value || !actual.value?.entradas) return null;
  return { href: actual.value.entradas, vendedor: actual.value.vendedor || "" };
});

const alComprar = () => medir("concierto_entradas", { artista: actual.value?.artista || "" });

// ── El carrusel ──

function quietos() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function parar() {
  if (reloj) cancelAnimationFrame(reloj);
  reloj = null;
}

function arrancar() {
  parar();
  avance.value = 0;
  if (!gira.value || quietos()) return;
  desde = performance.now();
  const tic = (ahora) => {
    const parte = (ahora - desde) / props.intervalo;
    if (parte >= 1) {
      siguiente();
      return;
    }
    avance.value = parte;
    reloj = requestAnimationFrame(tic);
  };
  reloj = requestAnimationFrame(tic);
}

function siguiente() {
  if (!total.value) return;
  paso.value = (paso.value + 1) % total.value;
  arrancar();
}

function anterior() {
  if (!total.value) return;
  paso.value = (paso.value - 1 + total.value) % total.value;
  arrancar();
}

watch(
  () => props.conciertos,
  () => {
    paso.value = 0;
    arrancar();
  },
);

function alCambiarVisibilidad() {
  if (document.hidden) parar();
  else arrancar();
}

onMounted(() => {
  arrancar();
  if (franja.value && typeof ResizeObserver !== "undefined") {
    observador = new ResizeObserver(([e]) => {
      visor.value = { ancho: e.contentRect.width, alto: e.contentRect.height };
    });
    observador.observe(franja.value);
  }
  document.addEventListener("visibilitychange", alCambiarVisibilidad);
});

onUnmounted(() => {
  parar();
  observador?.disconnect();
  clearTimeout(relevo);
  document.removeEventListener("visibilitychange", alCambiarVisibilidad);
});
</script>

<template>
  <section v-if="actual" ref="franja" class="agenda" aria-label="Próximos conciertos">
    <!-- La foto: la que se ve y, encima, la que entra. -->
    <DuotoneArt
      v-if="mostrado"
      class="agenda__arte is-lista"
      :src="mostrado"
      alt=""
      :hover-color="false"
      :duotone="false"
      ratio="auto"
      :position="anclaje(mostrado)"
    />
    <DuotoneArt
      v-if="entrante"
      :key="entrante"
      class="agenda__arte"
      :class="{ 'is-lista': cargada }"
      :src="entrante"
      :alt="`Foto de ${actual.artista}`"
      :hover-color="false"
      :duotone="false"
      ratio="auto"
      :position="anclaje(entrante)"
      @cargada="alCargar"
    />

    <!-- El velo: meseta al 0,5 hacia `bg/ink`, anclada al pie. -->
    <div class="agenda__velo" :class="{ 'is-varias': varias }" aria-hidden="true" />

    <!-- La regla del carrusel, arriba. -->
    <div v-if="gira" class="agenda__regla" aria-hidden="true">
      <i :style="{ width: `${avance * 100}%` }" />
    </div>

    <div class="agenda__datos">
      <!-- La cabeza: el nombre y el chip (con una fecha) y, a 12, la frase. -->
      <div class="agenda__cabeza">
        <div class="agenda__titular">
          <h2 class="agenda__artista">{{ actual.artista }}</h2>
          <ChipFecha v-if="!varias" class="agenda__fecha" :fecha="actual.fecha" />
        </div>
        <p v-if="actual.frase" class="agenda__frase">{{ actual.frase }}</p>
      </div>

      <!-- Tras el filete: dónde (o las fechas), y a la derecha la compra y el paso. -->
      <div class="agenda__pie" :class="{ 'is-varias': varias }">
        <ul v-if="varias" class="agenda__fechas">
          <li v-for="f in fechas" :key="f.fecha + f.donde" class="agenda__dia">
            <div class="agenda__dia-caja">
              <span class="agenda__dia-fecha">
                <AstraIcon name="upcoming-shows" :size="14" />
                {{ diaYMes(f.fecha) }}
              </span>
              <span v-if="f.donde" class="agenda__dia-donde">{{ f.donde }}</span>
            </div>
            <template v-if="f.entradas">
              <BrandTicketmaster
                v-if="f.vendedor === 'ticketmaster'"
                tamano="sm"
                :href="f.entradas"
                @click="alComprar"
              />
              <CtaButton
                v-else
                class="agenda__comprar agenda__comprar--sm"
                label="Comprar entradas"
                variant="outline"
                :href="f.entradas"
                target="_blank"
                rel="noopener noreferrer"
                @click="alComprar"
              />
            </template>
          </li>
        </ul>
        <p v-else class="agenda__linea">
          <template v-for="(parte, i) in linea" :key="i">
            <span v-if="i" class="agenda__punto" aria-hidden="true">·</span>
            <span>{{ parte }}</span>
          </template>
        </p>

        <div class="agenda__acciones">
          <template v-if="destino?.href">
            <BrandTicketmaster
              v-if="destino.vendedor === 'ticketmaster'"
              :href="destino.href"
              @click="alComprar"
            />
            <CtaButton
              v-else
              class="agenda__comprar"
              label="Comprar entradas"
              variant="outline"
              :href="destino.href"
              target="_blank"
              rel="noopener noreferrer"
              @click="alComprar"
            />
          </template>

          <div v-if="gira" class="agenda__paso">
            <p class="agenda__contador">{{ contador }}</p>
            <ButtonDs
              class="agenda__flecha"
              variant="ghost"
              surface="imagen"
              icono="chevron-back"
              :icono-tam="18"
              etiqueta="Concierto anterior"
              :lado="40"
              @click="anterior"
            />
            <i class="agenda__marca" aria-hidden="true" />
            <ButtonDs
              class="agenda__flecha"
              variant="ghost"
              surface="imagen"
              icono="chevron-forward"
              :icono-tam="18"
              etiqueta="Concierto siguiente"
              :lado="40"
              @click="siguiente"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// ── La franja ──
// Ancho completo y el alto sale del ancho: 27 : 10 (`alto = ancho × 0,3704`).
// 1512 × 560 en el portátil de referencia, 1440 × 533 en la plantilla.
.agenda {
  position: relative;
  width: 100%;
  aspect-ratio: 27 / 10;
  overflow: hidden;
  background: var(--color-art-mid);
  color: var(--color-text-on-ink);
}

// La foto cubre, anclada arriba (Pablo, 21.10.26): empieza justo bajo la
// cabecera y lo que sobra se recorta por abajo. El anclaje lo pone `anclaje()`
// en línea, por el encuadre.
// ⚠️ `ratio="auto"`: el cuadrado por defecto de `DuotoneArt` estiraba la capa a
// 1440 × 1440 y la foto salía ampliada y caída hacia abajo.
.agenda__arte {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.32s linear;

  &.is-lista {
    opacity: 1;
  }

  :deep(.duotone),
  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// ── El velo ──
// ── Pablo, 21.10.26: más claro y que se funda con el fondo ── El velo ya no es
// meseta: una sola curva (`alfa = t²`, catorce paradas; con `3t² − 2t³` le
// pareció demasiado intensa) de nada arriba al color entero en el pie, de modo
// que la franja se funde con la página de debajo, sin escalón. 380 de alto (400 con varias fechas): al
// alargarlo, la parte de arriba aclara y la de los datos sigue oscura.
//
// 🔴 **El mismo en los dos modos** (Pablo, 22.10.26): el negro del fondo del
// modo oscuro, neutral-950. En claro se fundía con el lienzo claro y el velo
// salía blanco, sin dejar leer la letra `text/on-ink`.
// ⚠️ Ningún token semántico vale neutral-950 en los dos modos salvo
// `brand/mark-ink`, que es de la marca; se usa por su valor hasta que diseño dé
// uno propio (pedido por el buzón el 22.10.26).
.agenda__velo {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  --velo: var(--color-brand-mark-ink);
  height: 380px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    // ⚠️ `transparent` y no `color-mix(… 0%, …)`: el minificador de producción
    // deja ese `0%` en `0`, que en `color-mix` no vale, y el navegador tiraba el
    // degradado entero. En local no se minifica y se veía bien (21.10.26).
    transparent 0%,
    color-mix(in srgb, var(--velo) 0.6%, transparent) 7.7%,
    color-mix(in srgb, var(--velo) 2.4%, transparent) 15.4%,
    color-mix(in srgb, var(--velo) 5.3%, transparent) 23.1%,
    color-mix(in srgb, var(--velo) 9.5%, transparent) 30.8%,
    color-mix(in srgb, var(--velo) 14.8%, transparent) 38.5%,
    color-mix(in srgb, var(--velo) 21.3%, transparent) 46.2%,
    color-mix(in srgb, var(--velo) 29%, transparent) 53.8%,
    color-mix(in srgb, var(--velo) 37.9%, transparent) 61.5%,
    color-mix(in srgb, var(--velo) 47.9%, transparent) 69.2%,
    color-mix(in srgb, var(--velo) 59.2%, transparent) 76.9%,
    color-mix(in srgb, var(--velo) 71.6%, transparent) 84.6%,
    color-mix(in srgb, var(--velo) 85.2%, transparent) 92.3%,
    color-mix(in srgb, var(--velo) 100%, transparent) 100%
  );
}

.agenda__velo.is-varias {
  height: 400px;
}

// La marca corta que separa sobre la foto: 1 de ancho, `text/on-ink` al 40 % en
// la opacidad del relleno (no es un token: DESIGN.md § Sobre la foto, marcas
// cortas en lugar de filetes).
@mixin marca($alto) {
  content: "";
  position: absolute;
  top: 50%;
  width: 1px;
  height: $alto;
  background: color-mix(in srgb, var(--color-text-on-ink) 40%, transparent);
  transform: translateY(-50%);
}

// ── La regla ── 2 de alto, pista `border/strong` y avance `bg/accent`.
.agenda__regla {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  height: 2px;
  background: var(--color-border-strong);

  i {
    display: block;
    height: 100%;
    background: var(--color-bg-accent);
  }
}

// ── Los datos ── a 40 de los lados y 32 del pie, en `text/on-ink`.
.agenda__datos {
  position: absolute;
  left: 40px;
  right: 40px;
  bottom: 32px;
  z-index: 2;
}

// La cabeza: el titular y, a 12, la frase. **Sin filete** desde el 21.10.26
// (§ Sobre la foto, marcas cortas en lugar de filetes): 40 hasta la línea de
// abajo, 18 aquí y 22 en el pie. Sin frase, la línea sube.
.agenda__cabeza {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 18px;
}

// El nombre y, a 20, el chip, 5 por debajo de la caja de línea del nombre. Si
// no caben juntos, el chip baja debajo, a 12.
.agenda__titular {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px 20px;
}

// La frase: `mono/lead`, caja normal, una línea con puntos suspensivos.
.agenda__frase {
  @include t.mono-lead;
  margin: 0;
  overflow: hidden;
  color: var(--color-text-on-ink);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda__artista {
  @include t.display-page;
  margin: 0;
  min-width: 0;
  color: var(--color-text-on-ink);
}

.agenda__fecha {
  margin-top: 5px;
}

// La línea de abajo, a 22: dónde a la izquierda y lo de la derecha a 24.
.agenda__pie {
  display: flex;
  align-items: center;
  gap: 24px;
  padding-top: 22px;
}

.agenda__linea {
  @include t.mono-label;
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 10px;
  min-width: 0;
  margin: 0;
  color: var(--color-text-on-ink);
  text-transform: uppercase;
  white-space: nowrap;

  span {
    flex: none;
  }
}

// ── Varias fechas ── en columnas, alineadas abajo, con 24 a la derecha. Desde
// la segunda, 24 a la izquierda y en su borde **una marca corta de 1 × 20**,
// centrada en vertical (21.10.26: un filete entero sobre la foto se leía como
// raya negra en las claras y desaparecía en las oscuras). Las que no caben
// bajan a una segunda línea, a 12.
.agenda__pie.is-varias {
  align-items: flex-end;
}

.agenda__fechas {
  display: flex;
  flex: 1 1 0;
  flex-wrap: wrap;
  row-gap: 12px;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

// La fecha y, a 20, su compra, centrada en vertical.
.agenda__dia {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-right: 24px;

  & + & {
    position: relative;
    padding-left: 24px;

    &::before {
      @include marca(20px);
      left: 0;
    }
  }
}

.agenda__dia-caja {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--color-text-on-ink);
}

// Icono 14 y, a 8, «27 NOV» en `display/item`.
.agenda__dia-fecha {
  @include t.display-item;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.agenda__dia-donde {
  @include t.mono-label;
  text-transform: uppercase;
  white-space: nowrap;
}

.agenda__acciones {
  display: flex;
  flex: none;
  align-items: center;
  gap: 16px;
}

// El cta sobre tinta: 42 de alto, 16 a los lados, contorno y texto `text/on-ink`.
.agenda__comprar {
  height: 42px;
  padding: 0 16px;
  border-color: var(--color-text-on-ink);
  color: var(--color-text-on-ink);
}

// El cta a 32 de la compra de cada fecha: 12 a los lados y 8 hasta la flecha.
.agenda__comprar--sm {
  gap: 8px;
  height: 32px;
  padding: 0 12px;
}

// Las flechas, **sin caja** desde el 21.10.26: el contador con 14 a cada lado,
// dos botones de 40 × 40 y entre los dos una marca de 1 × 16.
.agenda__paso {
  display: flex;
  align-items: stretch;
  height: 40px;
}

.agenda__marca {
  position: relative;
  flex: none;
  width: 1px;

  &::before {
    @include marca(16px);
    left: 0;
  }
}

.agenda__contador {
  @include t.mono-micro;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0 14px;
  color: var(--color-text-on-ink);
  white-space: nowrap;
}

// Sin contorno, tampoco al pasar: el filete se queda transparente y el hover es
// solo el chevron en rojo.
.agenda__paso .agenda__flecha,
.agenda__paso .agenda__flecha:hover {
  height: 40px;
  border-color: transparent;
}

.agenda__paso .agenda__flecha:not(:hover) {
  color: var(--color-text-on-ink);
}

// ── Estrechado ── provisional: no hay tablero de la franja en móvil.
// Por debajo de 880 la franja gana alto para que quepan los datos, y la línea de
// abajo se apila: dónde arriba y la compra y el paso debajo.
@media (max-width: 880px) {
  .agenda {
    aspect-ratio: auto;
    min-height: 460px;
  }

  .agenda__datos {
    left: 20px;
    right: 20px;
    bottom: 20px;
  }

  .agenda__pie {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .agenda__linea {
    flex-wrap: wrap;
    white-space: normal;
  }

  .agenda__acciones {
    justify-content: space-between;
  }
}
</style>
